import type { Key } from '../i18n'

/**
 * The version-retention policy as the web sees it (AB#245) — a mirror of
 * backend/domain/retention.ts, which stays the authority: the server re-validates every PUT and
 * answers 400 `invalid`. The checks here only let the forms refuse a value in place, worded, before
 * a round trip.
 */
export type RetentionUnit = 'hours' | 'days' | 'workingDays' | 'weeks' | 'months'
export const RETENTION_UNITS: readonly RetentionUnit[] = ['hours', 'days', 'workingDays', 'weeks', 'months']

export type RetentionKeep = 'all' | 'runs' | { perDay: number } | { perWeek: number } | { total: number }
export type RetentionKeepKind = 'all' | 'runs' | 'perDay' | 'perWeek' | 'total'
export const RETENTION_KEEP_KINDS: readonly RetentionKeepKind[] = ['all', 'runs', 'perDay', 'perWeek', 'total']

export interface RetentionTier {
  after: { n: number; unit: RetentionUnit }
  keep: RetentionKeep
}

export interface RetentionPolicy {
  tiers: RetentionTier[]
  /** Local 'HH:MM' — the part of the day `perDay` slices. */
  dayWindow: { start: string; end: string }
  /** IANA zone the window, days, working days and weeks are read in. */
  timeZone: string
}

/** A bid's override: one count per tenant tier, index-aligned, null = the tenant's. */
export type BidRetentionCounts = Array<number | null>

export interface RetentionStamp {
  updatedBy?: string
  updatedAt?: string
}

/** `GET|PUT /v1/admin/retention`. */
export type TenantRetentionView = { policy: RetentionPolicy; isDefault: boolean } & RetentionStamp

/** `GET|PUT /v1/bids/:bid/retention` — the merged policy, the tenant's, and the bid's counts. */
export type BidRetentionView = {
  policy: RetentionPolicy
  tenant: RetentionPolicy
  counts: BidRetentionCounts | null
} & RetentionStamp

/** `GET /v1/bids/:bid/retention/preview` — the dry run of the SAVED policy. */
export interface RetentionPreview {
  wouldRemove: number
  rooms: number
}

/** domain/retention.ts#DEFAULT_RETENTION_POLICY — the product request (2026-10-13). */
export const DEFAULT_RETENTION_POLICY: RetentionPolicy = {
  tiers: [
    { after: { n: 3, unit: 'workingDays' }, keep: 'runs' },
    { after: { n: 1, unit: 'weeks' }, keep: { perDay: 3 } },
    { after: { n: 2, unit: 'weeks' }, keep: { perDay: 1 } },
    { after: { n: 3, unit: 'months' }, keep: { perWeek: 1 } },
    { after: { n: 6, unit: 'months' }, keep: { total: 1 } },
  ],
  dayWindow: { start: '08:00', end: '20:00' },
  timeZone: 'Europe/Paris',
}

export const RETENTION_MAX_TIERS = 12
export const RETENTION_MAX_AGE = 1000
export const RETENTION_MAX_COUNT = 100

export function keepKind(keep: RetentionKeep): RetentionKeepKind {
  if (typeof keep === 'string') return keep
  if ('perDay' in keep) return 'perDay'
  if ('perWeek' in keep) return 'perWeek'
  return 'total'
}

/** The count a `keep` carries (perDay / perWeek / total), or null for 'all' / 'runs'. */
export function keepCount(keep: RetentionKeep): number | null {
  if (typeof keep === 'string') return null
  if ('perDay' in keep) return keep.perDay
  if ('perWeek' in keep) return keep.perWeek
  return keep.total
}

/** A `keep` of `kind`; a counted kind takes `n` (the caller validates it). */
export function makeKeep(kind: RetentionKeepKind, n: number): RetentionKeep {
  switch (kind) {
    case 'all':
    case 'runs':
      return kind
    case 'perDay':
      return { perDay: n }
    case 'perWeek':
      return { perWeek: n }
    case 'total':
      return { total: n }
  }
}

export function isRetentionCount(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= RETENTION_MAX_COUNT
}

/** A form field's text as a whole number, or NaN — '' and '2.5' are not counts. */
export function parseWhole(text: string): number {
  return /^\s*\d+\s*$/.test(text) ? Number(text) : NaN
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

function minutesOf(hhmm: string): number {
  return Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))
}

export function isTimeZone(tz: string): boolean {
  if (tz.length === 0) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** Nominal length of an age in hours — only to check the tiers are ordered youngest first. */
const NOMINAL_HOURS: Record<RetentionUnit, number> = {
  hours: 1,
  days: 24,
  workingDays: (24 * 7) / 5,
  weeks: 24 * 7,
  months: 24 * 30,
}

/** Why `policy` is not a valid tenant policy (an i18n key), or null — retentionPolicyError's rules. */
export function retentionPolicyError(policy: RetentionPolicy): Key | null {
  if (policy.tiers.length === 0 || policy.tiers.length > RETENTION_MAX_TIERS) return 'retention_err_tiers'
  let previous = -1
  for (const tier of policy.tiers) {
    const { n, unit } = tier.after
    if (!Number.isInteger(n) || n < 0 || n > RETENTION_MAX_AGE) return 'retention_err_age'
    const count = keepCount(tier.keep)
    if (count !== null && !isRetentionCount(count)) return 'retention_err_count'
    const hours = n * NOMINAL_HOURS[unit]
    if (hours <= previous) return 'retention_err_order'
    previous = hours
  }
  const { start, end } = policy.dayWindow
  if (!HHMM.test(start) || !HHMM.test(end)) return 'retention_err_window_format'
  if (minutesOf(start) >= minutesOf(end)) return 'retention_err_window_order'
  if (!isTimeZone(policy.timeZone)) return 'retention_err_zone'
  return null
}

/** Why `counts` cannot override `tenant` (an i18n key), or null — bidCountsError's rules. */
export function bidCountsError(counts: BidRetentionCounts, tenant: RetentionPolicy): Key | null {
  if (counts.length > tenant.tiers.length) return 'retention_err_tiers'
  for (let i = 0; i < counts.length; i++) {
    const n = counts[i]
    if (n === null || n === undefined) continue
    if (keepCount(tenant.tiers[i]!.keep) === null) return 'retention_err_countless'
    if (!isRetentionCount(n)) return 'retention_err_count'
  }
  return null
}

type T = (k: Key) => string

/** "3 working days", "1 week" — an age in words. */
export function ageText(t: T, after: { n: number; unit: RetentionUnit }): string {
  const unit = after.n === 1 ? t(`retention_unit_${after.unit}_one`) : t(`retention_unit_${after.unit}`)
  return `${after.n} ${unit}`
}

/** "Every version", "3 per day" — what a tier keeps, in words. */
export function keepText(t: T, keep: RetentionKeep): string {
  const kind = keepKind(keep)
  if (kind === 'all' || kind === 'runs') return t(`retention_keep_${kind}`)
  return `${keepCount(keep)} ${t(`retention_keep_${kind}`)}`
}
